import React from "react";
import { Badge } from "./badge";
import { cn } from "@/lib/utils";

/**
 * StatusBadge — UI-2C4 semantic status indicator.
 *
 * Thin wrapper over the shadcn `Badge` that maps a small, fixed set of
 * meaningful states to a consistent, accessible (AA) colour on the light/warm
 * surface. Status is always conveyed by the label text (children), not colour
 * alone. Presentation only — it carries no business logic.
 *
 * Props:
 * - status    "success" | "warning" | "error" | "info" | "neutral"
 * - icon      optional lucide component (rendered before the label)
 * - children  the status label (required for meaning)
 */
const statusStyles = {
  success: "bg-emerald-100 text-emerald-800 border-transparent",
  warning: "bg-amber-100 text-amber-800 border-transparent",
  error: "bg-red-100 text-red-800 border-transparent",
  info: "bg-blue-100 text-blue-800 border-transparent",
  neutral: "bg-stone-100 text-stone-700 border-transparent",
};

export function StatusBadge({
  status = "neutral",
  icon: Icon,
  className,
  children,
  ...props
}) {
  return (
    <Badge
      data-testid={`status-badge-${status}`}
      className={cn(statusStyles[status] || statusStyles.neutral, "gap-1.5", className)}
      {...props}
    >
      {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}
      {children}
    </Badge>
  );
}

export default StatusBadge;
